/*
  Copyright Epic Games, Inc. All Rights Reserved.

  The game-mode link allowlist, split out of CosmosRichText.game.ts so callers
  that only need the predicate can import it without pulling in that module's
  @xmldom/xmldom dependency. CTA components have to decide whether to render
  during the render pass, so they can't wait on the dynamic import the rich-text
  path uses.
*/

import { isAbsolute } from "@cosmos/common-utils";

/**
 * Epic paths that stay reachable from an in-game / console webview. Matched as a
 * contiguous run of whole path segments, so a locale prefix (`/en-US/tos`) still
 * matches while a longer word does not.
 */
export const PAGES_IN_GAME = [
	"/tos",
	"/tos/preview",
	"/privacypolicy",
	"/privacy-policy",
	"/fan-art-policy",
	"/privacy-policy/preview",
	"/young-player-policy",
	"/community-rules",
	"/refund-policy",
	"/content-guidelines",
	"/recommendations",
	"/developer-rules",
	"/developer-rules-change-log",
];

const EPIC_DOMAINS = ["epicgames.com", "epicgames.net"];

/**
 * Is this hostname Epic-owned?
 *
 * Matches the apex domain or any subdomain of it. Deliberately NOT a substring
 * test: `hostname.includes("epicgames.com")` also matches
 * `epicgames.com.attacker.test`, which would leave an attacker-controlled link
 * clickable in a webview with no nav as long as its path contained `/tos`.
 */
const isEpicHost = (hostname: string): boolean =>
	EPIC_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));

/**
 * Is this hostname a `legal.` host on an Epic domain — `legal.epicgames.com`,
 * `legal.a.b.epicgames.net`, …?
 *
 * Narrower than {@link isEpicHost} on purpose: game mode only ships on legalhub,
 * so `legal.` is the only Epic host whose allowlisted pages are themselves
 * game-mode-aware and therefore safe to reach from a webview with no back button.
 * Any other Epic host serving `/tos` would strand the player.
 *
 * Both halves are load-bearing. `startsWith("legal.")` matches only the *leading*
 * label, so `www.legal.epicgames.com` is rejected; and the suffix half must stay
 * an {@link isEpicHost} call, or `legal.epicgames.com.attacker.test` passes.
 */
const isEpicLegalDomain = (hostname: string): boolean => hostname.startsWith(`legal.`) && isEpicHost(hostname);

/**
 * Class on the span that replaces a neutralised anchor. Styled in
 * CosmosRichText.atom.tsx — deliberately a class rather than an inline
 * `opacity`, which stacked with already-dimmed text and fell under WCAG AA.
 */
export const NEUTRALISED_LINK_CLASS = "cosmos-neutralised-link";

/**
 * The ` [href]` suffix shown after a neutralised link's text, or `""` when the
 * text already spells the destination out — common in legal copy, where it would
 * otherwise be announced twice.
 */
export const neutralisedHrefSuffix = (text: string, href: string): string =>
	text.trim().includes(href) ? "" : ` [${href}]`;

const segmentsOf = (pathname: string): string[] => pathname.split("/").filter(Boolean);

/**
 * Does this pathname contain an allowlisted path as a run of whole segments?
 *
 * Segment-wise rather than `pathname.includes(entry)`, which also matched any
 * longer word built on an entry: `/tostada` and `/tos-promo` both satisfied
 * `/tos` and stayed clickable. Matching a *run* (not just the last segment) keeps
 * multi-segment entries like `/tos/preview` working, and starting anywhere in the
 * path keeps locale prefixes like `/en-US/tos` working.
 */
const matchesAllowlistedPath = (pathname: string): boolean => {
	const actual = segmentsOf(pathname);

	return PAGES_IN_GAME.some((entry) => {
		const wanted = segmentsOf(entry);
		if (!wanted.length) return false;

		return actual.some((_, start) => wanted.every((segment, offset) => actual[start + offset] === segment));
	});
};

/**
 * Resolve an href against the current location, absolute or relative.
 *
 * @returns The parsed URL, or `null` when the href can't be parsed at all.
 */
const resolveHref = (location: URL, href: string): URL | null => {
	try {
		return new URL(isAbsolute(href) ? href : `${location.origin}${href}`);
	} catch {
		return null;
	}
};

/**
 * Is this href safe to leave clickable in an in-game / console webview?
 *
 * Only allowlisted paths on an Epic `legal.` host qualify (see
 * {@link isEpicLegalDomain}). Everything else — third-party links, Epic pages
 * outside {@link PAGES_IN_GAME}, allowlisted paths on a non-`legal.` Epic host
 * such as `www.epicgames.com/tos`, and anything that fails to parse — is not,
 * because a webview with no nav can't come back from it. Errs restrictive by
 * design: an unparseable href returns `false`.
 */
export const isGameModeAllowedHref = (location: URL, href?: string | null): boolean => {
	if (!href) return false;

	const hrefObj = resolveHref(location, href);
	if (!hrefObj) return false;

	if (!isEpicLegalDomain(hrefObj.hostname)) return false;

	return matchesAllowlistedPath(hrefObj.pathname);
};

/**
 * The same href carrying `mode=game`, so following an allowlisted link keeps the
 * webview in game mode. Returns the href untouched if it can't be parsed.
 */
export const withGameModeParam = (location: URL, href: string): string => {
	const hrefObj = resolveHref(location, href);
	if (!hrefObj) return href;

	hrefObj.searchParams.set("mode", "game");
	return hrefObj.toString();
};
